import { Box, Button, Flex, HStack } from '@chakra-ui/react'
import { t } from '@lingui/core/macro'
import { type FC, useState, type KeyboardEvent } from 'react'
import { MdArrowUpward } from 'react-icons/md'

import { useCaptureEvent } from '../../../utils/analytics'
import type { WidgetState } from '../../Copilot'
import { LiveSummaryActionButton } from '../../LiveSummaryActionButton'
import { DynamicTextArea } from '../LiveSummary/DynamicTextArea'
import { useAskQuestion } from './useAskCopilot'

type Props = {
  state: WidgetState
  isDisabled?: boolean
}

export const InputBox: FC<Props> = ({ state, isDisabled = false }) => {
  const { modules, config, data } = state

  const [text, setText] = useState('')
  const [userInitiatedChatUid, setUserInitiatedChatUid] = useState<
    string | null
  >(null)
  const captureEvent = useCaptureEvent()
  const askQuestion = useAskQuestion({
    copilotToken: config.copilotApiToken,
    sessionUid: data.sessionUid,
  })

  const handleSubmit = () => {
    const trimmed = text.trim()
    if (trimmed.length > 0) {
      askQuestion.mutate(
        { question: trimmed, uid: userInitiatedChatUid },
        {
          onSuccess: (resp) => {
            setUserInitiatedChatUid(resp.uid)
          },
        },
      )

      captureEvent('copilot_askCopilot_question', {
        sessionUid: data.sessionUid,
        chatUid: userInitiatedChatUid ?? '',
        question: trimmed,
      })
      setText('')
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Box
      borderRadius="lg"
      position="absolute"
      bottom="80px"
      left="50%"
      transform="translateX(-50%)"
      width="clamp(240px, 452px, calc(100% - 64px))" // 240px is the minimum width, 452px is the maximum width, 64px is the margin
      bg="white"
      p="3"
      border="1px solid"
      borderColor="other.dividerStroke"
      boxShadow="glassShadow"
      zIndex="10"
    >
      <Flex direction="column" gap="2" alignItems="flex-end">
        <DynamicTextArea
          value={text}
          border="none"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t`Ask anything...`}
          resize="none"
          minH="28px"
          maxH="60px"
          p={0}
          overflowY="auto"
          isDisabled={isDisabled}
          borderRadius="md"
          _focus={{ border: 'none' }}
          _focusVisible={{ border: 'none' }}
          _hover={{ border: 'none' }}
        />
        <HStack
          justifyContent={
            modules.liveSummary.enabled ? 'space-between' : 'row-reverse'
          }
          alignItems="flex-start"
          w="100%"
        >
          {modules.liveSummary.enabled && (
            <Box w="50%">
              <LiveSummaryActionButton state={state} />
            </Box>
          )}
          <Button
            onClick={handleSubmit}
            isLoading={askQuestion.isPending}
            isDisabled={isDisabled}
            colorScheme="primary"
            size="xs"
            px={0}
            minH="24px"
            borderRadius="md"
          >
            <MdArrowUpward />
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
